import React, { useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import { UseFormReturn } from 'react-hook-form'
import { object, boolean, string } from 'yup'
import useOrder from 'modules/order/state/order/use-order'
import CheckboxField from 'modules/ui/molecules/FormComponents/CheckboxField'
import InputField from 'modules/ui/molecules/FormComponents/InputField'
import Column from 'modules/ui/atoms/Layout/Column'
import Row from 'modules/ui/atoms/Layout/Row'
import DonationOptionFormWrapper from './donation-option-form-wrapper'

interface OrgDonationFormData {
  isCompanyCard: boolean
  cardCompanyName: string
}

interface OrgDonationOptionFieldsProps {
  form: UseFormReturn<OrgDonationFormData>
}

const OrgDonationOptionFields = ({ form }: OrgDonationOptionFieldsProps) => {
  const { t } = useTranslation()
  const order = useOrder()
  const { watch, formState } = form

  const isCompanyCard = watch('isCompanyCard')
  const cardCompanyName = watch('cardCompanyName')

  useEffect(() => {
    order.contact.updateField('isCompanyCard', isCompanyCard)
    order.contact.updateField('cardCompanyName', cardCompanyName || '')
  }, [isCompanyCard, cardCompanyName, order.contact])

  return (
    <Column>
      <Row>
        <CheckboxField
          id="isCompanyCard"
          dataId="organization-donation-checkbox"
          error={
            formState.errors.isCompanyCard
              ? { message: formState.errors.isCompanyCard.message || '' }
              : undefined
          }
        >
          {t('OrderContactQuestions.companyName.donationMessage', 'Donate as an organization')}
        </CheckboxField>
      </Row>

      {isCompanyCard && (
        <Row>
          <Column>
            <InputField
              id="cardCompanyName"
              label={t('OrderContactQuestions.companyName.alternateLabel', 'Organization Name')}
              dataId="organization-donation-name-input"
              autoComplete="organization"
              required
              error={
                formState.errors.cardCompanyName
                  ? { message: formState.errors.cardCompanyName.message || '' }
                  : undefined
              }
            />
          </Column>
        </Row>
      )}
    </Column>
  )
}

const OrgDonationOption = () => {
  const { t } = useTranslation()

  const validationSchema = object({
    isCompanyCard: boolean().required(),
    cardCompanyName: string().when('isCompanyCard', ([isCompanyCard], schema) => {
      return isCompanyCard
        ? schema
            .required(
              t(
                'OrderContactQuestions.companyName.validationMessage',
                'Organization name is required',
              ),
            )
            .matches(
              /\S+/,
              t(
                'OrderContactQuestions.companyName.notEmptyMessage',
                'Organization name cannot be empty',
              ),
            )
        : schema
    }),
  })

  const useOrgDonationValues = (): OrgDonationFormData => {
    const order = useOrder()

    return {
      isCompanyCard: order.contact.value.isCompanyCard,
      cardCompanyName: order.contact.value.cardCompanyName || '',
    }
  }

  return (
    <DonationOptionFormWrapper<OrgDonationFormData>
      formName="orgDonation"
      errorMessage="Please review your organization donation option"
      validationSchema={validationSchema}
      useInitialValues={useOrgDonationValues}
    >
      {(form) => <OrgDonationOptionFields form={form} />}
    </DonationOptionFormWrapper>
  )
}

export default OrgDonationOption
